import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ProfileService, UltiService, AwsService } from '__services';
import { isEmpty, sortBy } from 'lodash';
import * as moment from 'moment';
import { AlertsConst } from '~/app/containers/dashboard/alert/constants/alert.const';
import { ComplianceConst } from '~/app/containers/dashboard/compliance/constants/compliance.const';
import { IntermodalConst } from '~/app/containers/dashboard/intermodal/constants/intermodal.const';
import { PhysicalTransportConst } from '~/app/containers/dashboard/physical-transport/constants/physical-transport.const';
import { Constants } from '~/app/core/configs/constants';
import { DateTimePickerComponent } from '__components';

@Component({
  selector: 'app-intermodal-filter',
  templateUrl: './intermodal-filter.component.html',
  styleUrls: ['./intermodal-filter.component.scss'],
})
export class IntermodalFilterComponent implements OnInit {
  @Input() config: any;
  @Input() exporting: boolean = false;
  @Output() valueChange = new EventEmitter();
  @Output() export = new EventEmitter();
  @ViewChild('startDate', { static: false }) startDate: DateTimePickerComponent;
  @ViewChild('endDate', { static: false }) endDate: DateTimePickerComponent;
  @Output() reload = new EventEmitter();
  form: FormGroup;
  fromDate: any = null;
  toDate: any = null;
  isReheating: boolean;
  isEir: boolean;
  isLoading: boolean;
  isOnLoading: boolean;
  isEntry: boolean;
  isEntryIn: boolean;
  isEntryOut: boolean;
  isDocuments: boolean;
  isFilterChanged: boolean;
  isFlowsValidate: boolean;
  isFlowsAlerts: boolean;
  isLifts: boolean;
  isWeighbridge: boolean;
  isAlertNoti: boolean;
  isFleet: boolean;
  isJourneySearch: boolean;
  isJourneyReport: boolean;
  isVessel: boolean;
  isReportMaster: boolean;
  isCargo: boolean;
  isDGD: boolean;
  dateChanged: boolean;
  changedParam:
    | 'STATUS'
    | 'FROM_DATE'
    | 'TO_DATE'
    | 'QUERY'
    | 'SEARCH_TYPE'
    | 'TYPE'
    | 'LOCATION';

  hasFromDate = false;
  hasToDate = false;

  defaultParams: any = {
    offset: 0,
    limit: Constants.DefaultLimit,
  };
  filteredParams = {
    ...this.defaultParams,
    sort: null,
    q: null,
    type: null,
  };
  configDate = {
    pickerType: 'calendar',
  };
  fromDateConfigOptions = {
    ...this.configDate,
    maxDate: new Date(),
    onChange: (dateStr) => {
      this.fromDate = dateStr
        ? moment(dateStr).startOf('day')
        : this.config.fromDate;
      this.hasFromDate = !!dateStr;
      this.isFilterChanged = true;
      this.dateChanged = true;
      this.changedParam = 'FROM_DATE';
      this.setParams();
    },
    onClose: (dateStr) => {
      this.toDateConfigOptions.minDate = dateStr;
    },
  };
  toDateConfigOptions = {
    ...this.configDate,
    minDate: this.fromDate,
    onChange: (dateStr) => {
      this.toDate = dateStr ? moment(dateStr).endOf('day') : this.config.toDate;
      this.hasToDate = !!dateStr;
      this.isFilterChanged = true;
      this.dateChanged = true;
      this.changedParam = 'TO_DATE';
      this.setParams();
    },
    onClose: (dateStr) => {
      this.fromDateConfigOptions.maxDate = dateStr;
    },
  };

  isJourney: boolean;

  constructor(
    private fb: FormBuilder,
    private ultiService: UltiService,
    private route: ActivatedRoute,
    private profileService: ProfileService,
    private awsService: AwsService,
  ) {}

  ngOnInit(): void {
    this.detectPage();
    this.initForm();
    if (!this.isJourneyReport) {
      if (this.config.noDefaultDate) {
        this.fromDate = this.config.fromDate ?? null;
      } else {
        this.fromDate = this.config.fromDate
          ? this.config.fromDate
          : new Date().toISOString();
      }
    }
    if (this.isReheating) {
      this.toDate = undefined;
    }
    if (
      this.isEntry ||
      this.isEntryIn ||
      this.isEntryOut ||
      this.isAlertNoti ||
      this.isReheating
    ) {
      this.fromDate = undefined;
    }
    if (this.isVessel || this.isCargo) {
      this.fromDate = undefined;
      this.toDate = undefined;
    }
    if (this.isJourneySearch || this.isJourneyReport) {
      const firstValue = this.config.types[0].value;
      this.form.patchValue({ search_type: firstValue }, { emitEvent: false });
      if (this.config.types.length < 2) {
        this.form.controls['search_type'].disable();
      }
    }
    if (this.isJourneyReport) {
      this.form.patchValue({ status: 1 });
      this.isFilterChanged = true;
    }
    this.setDefaultLocation();
    if (this.isReportMaster) {
      this.setDefaultStatus();
    }
    this.applyQueryParams();
    this.setParams();
  }

  detectPage(): void {
    const page = this.config?.page;
    this.isReheating = page === IntermodalConst.Reheating;
    this.isEir = page === IntermodalConst.EquipmentInterchangeReceipt;
    this.isLoading = page === IntermodalConst.Loading;
    this.isOnLoading = page === IntermodalConst.OnLoading;
    this.isEntry = page === IntermodalConst.Entry;
    this.isEntryIn = page === IntermodalConst.EntryIN;
    this.isEntryOut = page === IntermodalConst.EntryOUT;
    this.isDocuments = page === ComplianceConst.Documents;
    this.isFlowsValidate = page === ComplianceConst.FlowValidate;
    this.isJourney = page === PhysicalTransportConst.Journey;
    this.isFlowsAlerts = page === AlertsConst.FlowsAlerts;
    this.isLifts = page === IntermodalConst.Lifts;
    this.isWeighbridge = page === IntermodalConst.Weighbridge;
    this.isAlertNoti = page === AlertsConst.AlertsNotification;
    this.isFleet = page === ComplianceConst.Fleet;
    this.isJourneySearch = page == PhysicalTransportConst.Search;
    this.isJourneyReport = page == PhysicalTransportConst.Report;
    this.isVessel = page == IntermodalConst.Vessel;
    this.isReportMaster = page == PhysicalTransportConst.ReportMaster;
    this.isCargo = page == IntermodalConst.Cargo;
    this.isDGD = page == IntermodalConst.Dgd;
  }

  initForm() {
    this.form = this.fb.group({
      location_id: [null],
      status: [null],
      from: [null],
      until: [null],
      search: [null],
      search_type: [this.config?.defaultSearchType ?? null],
      type: [null],
      category: [null],
      ownership: [null],
    });
  }

  setDefaultLocation(): void {
    if (this.config.locations && this.config.locations.length) {
      if (!this.config.locationsSorted) {
        this.config.locations = sortBy(this.config.locations, 'name');
      }
      let firstValue = this.config.locations[0].id;
      const { filter } = this.profileService.settings();
      if (
        filter &&
        filter.location_id &&
        this.isExistLocationId(filter.location_id)
      ) {
        firstValue = filter.location_id;
      }
      this.form.patchValue({ location_id: firstValue }, { emitEvent: false });
    }
  }

  isExistLocationId(id): boolean {
    const { locations } = this.config;
    if (locations) {
      return false;
    }
    for (let item of locations) {
      if (id == item.id) {
        return true;
      }
    }
    return false;
  }

  setDefaultStatus(): void {
    if (this.config.status && this.config.defaultStatus) {
      this.form.patchValue(
        { status: this.config.defaultStatus },
        { emitEvent: false },
      );
    }
  }

  applyQueryParams() {
    const query = this.route.snapshot.queryParams;
    const { from, until, location_id, status, type, category, ownership } =
      query;
    this.form.patchValue(query, { emitEvent: false });

    this.isFilterChanged = query.isFilterChanged === 'true' ? true : false;
    if (from) {
      this.fromDate = moment(from);
      const isSameDateConfig = moment(from).isSame(
        moment(this.config?.fromDate),
        'day',
      );
      if (!isSameDateConfig) {
        this.toDateConfigOptions.minDate = from;
        setTimeout(() => {
          this.startDate.setDefaultValue(from);
        }, 200);
      }
    }
    if (until) {
      this.toDate = moment(until);
      const isSameDateConfig = moment(until).isSame(
        moment(this.config?.toDate),
        'day',
      );
      if (!isSameDateConfig) {
        this.fromDateConfigOptions.maxDate = until;
        setTimeout(() => {
          this.endDate.setDefaultValue(until);
        }, 200);
      }
    }
    if (location_id) {
      this.form.patchValue({ location_id: Number(location_id) });
    }
    if (type) {
      this.form.patchValue({ type: Number(type) });
    }
    if (category) {
      this.form.patchValue({ category: Number(category) });
    }
    if (ownership) {
      this.form.patchValue({ ownership: Number(ownership) });
    }
    if (status !== null && status !== undefined) {
      this.form.patchValue({ status: Number(status) });
    }
  }

  onValueChanged(value): void {
    this.isFilterChanged = true;
    this.changedParam = 'STATUS';
    this.setParams();
  }

  onLocationChanged(value): void {
    // this.isFilterChanged = true;
    const profile = this.profileService.get();
    if (profile && profile.settings) {
      profile.settings['filter'] = { location_id: value.id };
      this.awsService.updateUserSettings(profile.settings).subscribe(() => {
        this.profileService.cache(profile);
      });
    }
    this.changedParam = 'LOCATION';
    this.setParams();
  }

  onValueCategoryChanged(value): void {
    this.isFilterChanged = true;
    this.setParams();
  }

  onSearchTypeChanged(value): void {
    this.changedParam = 'SEARCH_TYPE';
    this.isFilterChanged = true;
    const { search } = this.form.getRawValue();
    console.log(search);

    if (search) {
      this.setParams();
    }
  }

  onTypeChanged(value): void {
    this.isFilterChanged = true;
    this.setParams();
  }

  setParams(): void {
    const { status, search, type, category, search_type, ownership } =
      this.form.getRawValue();
    const _toDateRaw = this.toDate
      ? this.toDate
      : this.config.toDate
        ? this.config.toDate
        : this.config.noDefaultDate
          ? null
          : new Date().toISOString();

    let toDate = _toDateRaw
      ? moment(new Date(_toDateRaw)).endOf('day').toDate()
      : null;
    if (
      this.changedParam !== 'TO_DATE' &&
      this.toDate === undefined &&
      (this.isEntry ||
        this.isEntryIn ||
        this.isEntryOut ||
        this.isReheating ||
        this.isVessel ||
        this.isCargo ||
        this.isDGD) &&
      !this.config.toDate
    ) {
      toDate = undefined;
    }
    console.log(this.fromDate);
    const query: any = {
      // The inq operator checks whether the value of the specified property matches any of the values provided in an array.
      status: this.isFilterChanged
        ? status
        : this.config.defaultStatus
          ? { inq: this.config.defaultStatus }
          : null,
      expected_date: {
        between: [this.fromDate, toDate],
      },
    };
    this.filteredParams.sort = `${Constants.SortBy.expectedDate} ${Constants.OrderBy.asc}`;
    if (this.isJourneyReport) {
      // this.filteredParams.sort = `${Constants.SortBy.expectedStartDate} ${Constants.OrderBy.asc}`;
      this.filteredParams.sort = ``;
    }

    if (
      this.isLoading ||
      this.isEir ||
      this.isDocuments ||
      this.isFlowsValidate ||
      this.isOnLoading
    ) {
      // delete Object.assign(o, {newKey: o.oldKey }).oldKey
      if (this.dateChanged) {
        delete Object.assign(query, { actual_date: query.expected_date })
          .expected_date;
      } else {
        delete query.expected_date;
      }
    }

    if (this.isReheating) {
      this.filteredParams.sort = `${Constants.SortBy.expectedStartDate} ${Constants.OrderBy.asc}`;
      delete query.expected_date;
      this.fromDate ? (query.actual_start_date = { gte: this.fromDate }) : '';
      toDate ? (query.actual_end_date = { lte: toDate }) : '';
    }

    if (this.isFlowsValidate) {
      this.filteredParams.sort = `${Constants.SortBy.name} ${Constants.OrderBy.asc}`;
    }

    if (this.isFlowsAlerts) {
      this.filteredParams.sort = `${Constants.SortBy.createdDate} ${Constants.OrderBy.desc}`;
      delete Object.assign(query, { created_date: query.expected_date })
        .expected_date;
    }

    // Scope of EntryPage
    if (this.isEntry) {
      delete Object.assign(query, { actual_date: query.expected_date })
        .expected_date;
    }
    if (this.isEntryIn || this.isEntryOut) {
      delete Object.assign(query, { actual_date: query.expected_date })
        .expected_date;
      delete Object.assign(query, { inspection_result: query.status }).status;
      this.filteredParams.sort = `${Constants.SortBy.actualDate} ${Constants.OrderBy.asc}`;
    }

    if (this.isJourneySearch || this.isJourneyReport) {
      delete query.expected_date;
      query['ownership'] = ownership;
      this.filteredParams['search_type'] = search_type;
    }

    if (
      this.isDocuments ||
      this.isJourney ||
      this.isLoading ||
      this.isWeighbridge ||
      this.isReportMaster ||
      this.isVessel ||
      this.isLifts ||
      this.isOnLoading ||
      this.isEntry ||
      this.isEntryIn ||
      this.isEntryOut ||
      this.isReheating ||
      this.isEir ||
      this.isCargo ||
      this.isDGD
    ) {
      this.filteredParams['search_type'] = search_type;
    }

    // #end Scope of EntryPage

    if (this.isJourney) {
      delete Object.assign(query, { live_status: query.status }).status;
    }

    if (query.hasOwnProperty('actual_date') && !this.dateChanged) {
      delete query.actual_date;
    }

    if (this.isLifts) {
      delete query.expected_date;
      if (this.dateChanged) {
        if (this.fromDate && this.toDate) {
          query.actual_date = {
            between: [this.fromDate.toISOString(), this.toDate.toISOString()],
          };
        } else if (this.fromDate) {
          query.actual_date = { gte: this.fromDate.toISOString() };
        } else if (this.toDate) {
          query.actual_date = { lte: this.toDate.toISOString() };
        }
      }
      if ([0, 1].includes(type)) {
        Object.assign(query, { type: type });
      }
    }

    if (this.isDGD) {
      delete query.expected_date;
      if (this.dateChanged) {
        if (this.fromDate && this.toDate) {
          query.expected_date = {
            between: [this.fromDate.toISOString(), this.toDate.toISOString()],
          };
        } else if (this.fromDate) {
          query.expected_date = { gte: this.fromDate.toISOString() };
        } else if (this.toDate) {
          query.expected_date = { lte: this.toDate.toISOString() };
        }
      }
    }

    if (this.isWeighbridge && [0, 1].includes(category)) {
      Object.assign(query, { category });
    }

    if (this.isAlertNoti) {
      if (this.fromDate) {
        query['fromDate'] = this.fromDate;
      }
      if (this.toDate) {
        query['toDate'] = this.toDate;
      }
    }

    if (this.isJourneyReport) {
      if (this.fromDate && this.toDate) {
        query.and = [
          { expected_start: { gte: this.fromDate.toISOString() } },
          { expected_end: { lte: this.toDate.toISOString() } },
        ];
      } else if (this.fromDate) {
        query.and = [{ expected_start: { gte: this.fromDate.toISOString() } }];
      } else if (this.toDate) {
        query.and = [{ expected_end: { lte: this.toDate.toISOString() } }];
      }
    }

    if (this.isReportMaster) {
      delete query.expected_date;
      if (this.dateChanged) {
        const from = this.fromDate;
        const fromStr = this.ultiService.dateToString(
          from ? moment(from).startOf('day') : null,
        );
        const to = this.toDate;
        const toStr = this.ultiService.dateToString(
          to ? moment(to).endOf('day') : null,
        );
        if (fromStr && toStr) {
          query.and = [
            { pol_etd: { gte: fromStr } },
            { pod_eta: { lte: toStr } },
          ];
        } else if (fromStr) {
          query.and = [{ pol_etd: { gte: fromStr } }];
        } else if (toStr) {
          query.and = [{ pod_eta: { lte: toStr } }];
        }
      }
      delete query.status;
      if (status !== null && status !== undefined) {
        let query_status = { flow_status_id: status };
        if (!Number.isInteger(status)) {
          query_status = { flow_status_id: { inq: JSON.parse(status) } };
        }
        Object.assign(query, query_status);
      }

      this.filteredParams.sort = ``;
    }

    if (this.form.get('location_id').value) {
      query['location_id'] = this.form.get('location_id').value;
    }

    if (this.isVessel || this.isCargo) {
      delete query.expected_date;
      if (this.dateChanged) {
        if (this.fromDate && this.toDate) {
          query.expected_date = {
            between: [this.fromDate.toISOString(), this.toDate.toISOString()],
          };
        } else if (this.fromDate) {
          query.expected_date = { gte: this.fromDate.toISOString() };
        } else if (this.toDate) {
          query.expected_date = { lte: this.toDate.toISOString() };
        }
      }
      if ([0, 1].includes(type)) {
        Object.assign(query, { action_type: type });
      }
    }

    this.filteredParams.q = search;
    let formFilter = {
      ...this.form.value,
      isFilterChanged: this.isFilterChanged,
    };
    if (this.fromDate && typeof this.fromDate !== 'string') {
      formFilter = {
        ...formFilter,
        from: this.fromDate.toISOString(),
      };
    }
    if (this.toDate) {
      formFilter = {
        ...formFilter,
        until: this.toDate.toISOString(),
      };
    }
    const emitData = {
      params: this.removeEmpty({
        ...this.filteredParams,
        where: this.removeEmpty(query),
      }),
      formFilter,
    };
    this.valueChange.emit(
      // this.removeEmpty({
      //   ...this.filteredParams,
      //   where: this.removeEmpty(query),
      // })
      emitData,
    );
  }

  onSearch(): void {
    this.isFilterChanged = true;
    this.changedParam = 'QUERY';
    this.setParams();
  }

  onReload(): void {
    this.isFilterChanged = false;
    this.dateChanged = false;
    this.changedParam = null;

    this.fromDateConfigOptions.maxDate = null;
    this.toDateConfigOptions.minDate = null;

    this.fromDate = null;
    this.toDate = null;
    this.hasFromDate = false;
    this.hasToDate = false;

    setTimeout(() => {
      this.startDate?.setDefaultValue('');
      this.endDate?.setDefaultValue('');
    }, 0);

    this.form.reset();

    if (this.config?.defaultSearchType) {
      this.form.patchValue(
        { search_type: this.config.defaultSearchType },
        { emitEvent: false },
      );
    }

    if (this.isJourneySearch || this.isJourneyReport) {
      const firstValue = this.config.types[0].value;
      this.form.patchValue({ search_type: firstValue }, { emitEvent: false });
    }

    if (this.isReportMaster) {
      this.form
        .get('status')
        ?.setValue(this.config.defaultStatus, { emitEvent: false });
      this.form
        .get('search_type')
        ?.setValue(this.config.defaultSearchType, { emitEvent: false });
    }

    this.setDefaultLocation();

    this.reload.emit();
  }
  
  removeEmpty(obj: any): any {
    Object.entries(obj).forEach(([key, val]) => {
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        this.removeEmpty(val);
      } else {
        const fieldsRemoveWhenNull = [
          'inspection_result',
          'live_status',
          'type',
          'category',
          'status',
          'action_type',
          'flow_status_id',
        ];
        if (fieldsRemoveWhenNull.includes(key)) {
          if (val === null) {
            delete obj[key];
          }
        } else if (!val) {
          delete obj[key];
        }
      }
    });
    return isEmpty(obj) ? null : obj;
  }

  onExport(): void {
    this.export.emit();
  }

  onEnter(event: Event) {
    event.preventDefault();
  }

  clearDate(field: 'from' | 'until'): void {
    this.form.get(field)?.setValue(null);

    if (field === 'from') {
      this.fromDate = null;
      this.hasFromDate = false;
      setTimeout(() => {
        this.startDate?.setDefaultValue('');
      }, 0);
    } else {
      this.toDate = null;
      this.hasToDate = false;
      setTimeout(() => {
        this.endDate?.setDefaultValue('');
      }, 0);
    }

    this.dateChanged = true;
    this.isFilterChanged = true;
    this.changedParam = field === 'from' ? 'FROM_DATE' : 'TO_DATE';
    this.setParams();
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (!value || value.trim() === '') {
      this.isFilterChanged = true;
      this.changedParam = 'QUERY';
      this.form.get('search')?.setValue(null, { emitEvent: false });
      this.filteredParams.q = null;
      this.setParams();
    }
  }
}
